/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useMemo } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { PageWizardHeader } from '@veeam/components';

import { PageDialogContext, PageDialogStore } from './stores';
import { PageDialogLayout } from './components';

import type { PageDialogLayoutProps } from './components';

const PageDialogStyled = styled.div`
    display: flex;
    height: 100%;
`;

export interface PageDialogProps extends Partial<PageDialogLayoutProps> {
    title: string;
    description?: string;
    hasBack?: boolean;
    disabledBack?: boolean;
    content: React.ReactElement;
    footer?: React.ReactElement;
    onClose?: () => void;
}

export const PageDialog = observer((props: PageDialogProps) => {
    const { title, description, hasBack = true, disabledBack = false, content, footer = null, onClose, ...restProps } = props;
    const pageDialogStore = useMemo(() => new PageDialogStore(), []);
    const onBackClickHandler = () => {
        onClose?.();
    };

    return (
        <PageDialogContext.Provider value={pageDialogStore}>
            <PageDialogStyled>
                <PageDialogLayout
                    header={(
                        <PageWizardHeader
                            hasBack={hasBack}
                            onBackClick={onBackClickHandler}
                            disabledBack={disabledBack ? pageDialogStore.loading : false}
                            title={title}
                            description={description}
                            style={{ borderBottom: 'none' }}
                        />
                    )}
                    tabs={null}
                    content={content}
                    fullWidthContent={true}
                    footer={footer}
                    panel={
                        pageDialogStore.isPanelOpened && (
                            <>
                                {pageDialogStore.panelRenderer(() => pageDialogStore.closePanel())}
                            </>
                        )
                    }
                    pending={pageDialogStore.pending}
                    loading={pageDialogStore.loading}
                    {...restProps}
                />
            </PageDialogStyled>
        </PageDialogContext.Provider>
    );
});
